import { useCallback, useEffect, useState } from 'react';
import { ApiError, api } from '../lib/api';
import { AppShell } from './Layout';
import { Banner, Field, Loading } from './ui';
import { useSession } from '../state/session';

/**
 * Enrolment gate for tenant administrators.
 *
 * Owners, admins and compliance reviewers need a verified second factor before
 * any organization work, so without this they would simply meet errors
 * everywhere. This turns that into a single guided step, and it is the only
 * thing the dashboard renders until the factor exists.
 */
export function MfaWall({ enrolled, onSatisfied }: {
  enrolled: boolean; onSatisfied: () => void;
}) {
  const { refresh, signOut, user } = useSession();
  const [secret, setSecret] = useState<string | null>(null);
  const [otpauth, setOtpauth] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(!enrolled);

  useEffect(() => {
    if (enrolled) { setLoading(false); return; }
    api.post('/auth/mfa/begin')
      .then((data) => { setSecret(data.secret); setOtpauth(data.otpauth_url); })
      .catch((cause) => setError(cause instanceof ApiError ? cause.message
                                                           : 'Could not start setup.'))
      .finally(() => setLoading(false));
  }, [enrolled]);

  const submit = useCallback(async (event: React.FormEvent) => {
    event.preventDefault();
    setBusy(true); setError(null);
    try {
      await api.post(enrolled ? '/auth/mfa/verify' : '/auth/mfa/confirm', { code });
      await refresh();
      onSatisfied();
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : 'That code did not match.');
      setBusy(false);
    }
  }, [code, enrolled, refresh, onSatisfied]);

  return (
    <AppShell>
      <div className="shell shell--narrow section">
        <div className="card stack">
          <header className="stack-s">
            <h1 style={{ fontSize: '1.4rem', margin: 0 }}>
              {enrolled ? 'Confirm your authenticator code'
                        : 'Set up two-factor authentication'}
            </h1>
            <p className="muted small" style={{ margin: 0 }}>
              {enrolled
                ? 'Confirm the current code to continue.'
                : 'Two-factor authentication is required for everyone who '
                  + 'administers an organization. It takes about a minute.'}
            </p>
          </header>

          {error && <Banner tone="danger" title="That did not work">{error}</Banner>}
          {loading ? <Loading label="Preparing setup…" /> : (
            <form className="stack" onSubmit={submit}>
              {!enrolled && secret && (
                <>
                  <p className="small muted" style={{ margin: 0 }}>
                    Add this secret to an authenticator app — 1Password, Authy,
                    Google Authenticator or similar — then enter the six-digit
                    code it shows.
                  </p>
                  <pre className="codebox">{secret}</pre>
                  {otpauth && (
                    <details>
                      <summary className="small" style={{ cursor: 'pointer' }}>
                        Show the setup link
                      </summary>
                      <p className="small muted" style={{ wordBreak: 'break-all',
                                                          marginTop: 8 }}>
                        <code>{otpauth}</code>
                      </p>
                    </details>
                  )}
                </>
              )}
              <Field label="Six-digit code" id="mfa-wall-code" required>
                <input id="mfa-wall-code" className="input" inputMode="numeric"
                       autoComplete="one-time-code" maxLength={6} required value={code}
                       style={{ fontSize: '1.4rem', letterSpacing: '.3em',
                                textAlign: 'center' }}
                       onChange={(event) =>
                         setCode(event.target.value.replace(/\D/g, ''))} />
              </Field>
              <button className="btn btn--primary btn--large btn--block"
                      disabled={busy || code.length !== 6}>
                {busy ? 'Checking…' : enrolled ? 'Confirm and continue'
                                               : 'Enable and continue'}
              </button>
            </form>
          )}

          <hr className="divider" />
          <p className="small muted" style={{ margin: 0 }}>
            Signed in as {user?.email}.{' '}
            <button type="button" className="btn btn--small btn--ghost"
                    onClick={() => void signOut()}>Sign out</button>
          </p>
        </div>
      </div>
    </AppShell>
  );
}
